// The chrome half of the theme preview (R806): the frame over the preview
// canvas, and the real DOM modules the four chrome foci run inside it.
//
// - **notices**: the drain line along the foot of a top bar and the notices
//   column (R430), through `ui/notices.ts` — the code `Overlay` runs.
// - **dock**: the chrome dock-in (R182), through `ChromeDockIn` on the
//   frame's own chrome root. The frame has no black cover: the travel of
//   the panels is the look, and the page's `#blackout` is not the frame's.
// - **banner**: the season banner (R184), through `SeasonBanner`.
// - **ui**: a top bar and a dock panel in the chrome's own classes, so the
//   UI tokens show on the ink they colour.
//
// **The ids.** The chrome's stylesheet selects by id (`#ui`, `#topbar`,
// `#notices`, `#banner`, `#furniture`), so the frame takes those ids while
// no other `#ui` stands in the document — which is always true on the hub
// page and on the theme workshop. The game's own chrome is not up while a
// preview is, and a frame built beside one takes no id and draws its
// elements unstyled rather than steal the game's.
//
// **The position.** The chrome root is `absolute` in the frame and not
// `fixed` on the window. Everything else the frame moves is a RULE in
// `ui/styles.css` under `.theme-preview #ui` (R824), and not an inline
// style: a notices look states its own placement under
// `#ui.theme-notices-<id>`, and an inline style would stand over it. The
// chrome root also wears `preview-<focus>`, because the dock focus puts the
// notices column beside its own dock panel and the notices focus does not.
//
// **The dock's column.** The game's `ui/layout.ts` writes `--dock-w` on
// `#ui`, and a chart card in the bottom-right corner stands left of it. The
// frame writes the same property from its own dock panel, and it tells the
// chart furniture through `onLayout` when the figure moves, so a card never
// lies over the frame's dock (R1234).
//
// **The stacked order** (R2043). A MODDING frame is 248 px wide, narrower
// than the top bar and the dock panel side by side. Where the top bar is
// wider than the map left of the frame's dock panel, the frame writes
// `preview-stacked` on its chrome root, as the game takes the telephone
// order on a narrow screen: the top bar spans the frame, the dock panel
// stands under it, and no chart card shows (a telephone keeps no card of
// the stack). The decision is measured, as `ui/readoutfit.ts` measures the
// line of the game, never guessed from a width.
//
// **A bare frame** (R1914) is the frame of the close-up of the MODDING
// column: the canvas alone. Its chrome root stays hidden, so neither the
// chrome of a focus nor a DOM card of the chart shows over the map, it
// builds no chrome module, and it takes no id.
//
// Every word the frame writes comes from `gen/strings.ts`.

import { ChromeDockIn } from '../ui/dockin';
import { SeasonBanner } from '../ui/banner';
import { NoticeColumn, TimerLine, type NoticeOptions } from '../ui/notices';
import { applyThemeTo } from '../ui/theme';
import { LIGHT } from '../identity/pick';
import { lightBodyFor, wearLightOn } from '../ui/lightchrome';
import { AUTO_CHROME_LOOK, chromeLookOf, wearChromeLook, type ChromeLook } from '../ui/chromelook';
import { truceClaim } from '../ui/format';
import { esc } from '../ui/esc';
import { hhmmss } from '../locale/clock';
import { main as SM, overlay as SO } from '../gen/strings';
import type { PreviewFocus, PreviewLook } from './look';
import { PREVIEW_SEASON_LEFT_SEC, PREVIEW_SEASON_SEC } from './fixture';

/** How often the notices focus raises a line. */
const NOTICE_EVERY_SEC = 1.4;
/** One drain of the line, and the truce it counts down over that time. */
const DRAIN_SEC = 8;
const TRUCE_SEC = 6 * 3600;
/**
 * Where the frame's world clock stands when a focus starts, in seconds of
 * a world day. The `ledger` look stamps each line with the world clock the
 * top bar shows (R824), and the preview has no world to read one from, so
 * the frame carries this figure of its own and walks it with the loop.
 */
const WORLD_CLOCK_SEC = 12 * 3600 + 4 * 60 + 31;
/** The dock-in loop: how long the chrome stays out, and how long it rests. */
const DOCK_OUT_SEC = 0.5;
const DOCK_REST_SEC = 1.6;
/** The banner loop: the rest between two cards. */
const BANNER_REST_SEC = 1.2;
/** A line that must stand for the whole dock-in loop. */
const STANDING_MS = 3_600_000;
/** The class of the stacked order on the chrome root (R2043). */
export const PREVIEW_STACKED = 'preview-stacked';

/**
 * Whether the frame takes the stacked order (R2043): a dock panel stands,
 * and the top bar is wider than the map left of it. `barPx` is the width
 * the bar needs with its whole world name, `frameW` the width of the
 * chrome root and `dockW` the published dock column.
 */
export function stackedOrder(hasDock: boolean, barPx: number, frameW: number, dockW: number): boolean {
  return hasDock && barPx > frameW - dockW;
}

/** The lines the notices focus raises, in turn. One is a refusal. */
function noticeLines(): { text: string; opts: NoticeOptions }[] {
  return [
    { text: SM.site_point_defense_ready, opts: {} },
    { text: SM.base_tubes_ready({ free: 2 }), opts: {} },
    { text: SM.base_tubes_reloading({ sec: 8 }), opts: { error: true } },
    { text: SM.deposit_respawn, opts: {} },
  ];
}

type DockStep = 'rest' | 'out' | 'in';

/**
 * The id of the chrome field one focus draws, or '' where the focus draws
 * no chrome module of its own. The frame rebuilds when this changes.
 */
function chromeIdOf(focus: PreviewFocus, look: ChromeLook): string {
  if (focus === 'notices') return look.notices;
  if (focus === 'dock') return look.dock;
  if (focus === 'banner') return look.banner;
  return '';
}

export class PreviewFrame {
  /** The box the preview fills. It takes the host's whole size. */
  readonly wrap: HTMLDivElement;
  /** The drawing surface of the engine. */
  readonly canvas: HTMLCanvasElement;
  /** The chrome root: `#ui` inside the frame. The UI tokens go on it. */
  readonly chrome: HTMLDivElement;
  /** The chart furniture's DOM host (R548): `#furniture` inside the frame. */
  readonly furniture: HTMLDivElement;

  /** Whether the frame took the chrome's ids. */
  private readonly ids: boolean;
  /** The frame shows the canvas alone (R1914). */
  private readonly bare: boolean;
  private focus: PreviewFocus | null = null;
  /**
   * The chrome look the frame wears now (R814, R823), and the id the chrome
   * focus on screen was BUILT with.
   *
   * The two are not the same answer. A look reaches the frame on every
   * `show`, and a value that is only CSS needs no rebuild at all; a value
   * that changes the markup a module writes needs one. `builtWith` is the
   * id of the field of the focus on screen, so a second `show` of the same
   * focus with another id rebuilds, and a second `show` that changes
   * nothing still costs nothing.
   */
  private chromeLook: ChromeLook = AUTO_CHROME_LOOK;
  private builtWith = '';
  private worldName = '';
  /** The elements the current focus built, removed on the next focus. */
  private parts: HTMLElement[] = [];
  private t = 0;
  private next = 0;

  private notices: NoticeColumn | null = null;
  private lines = noticeLines();
  private line = 0;
  /** The timer line of the notices focus (R824). It owns the classes, the
   *  tab's markup and `--frac`, exactly as it does in the game. */
  private timer: TimerLine | null = null;

  private dockin: ChromeDockIn | null = null;
  private dockStep: DockStep = 'rest';

  private banner: SeasonBanner | null = null;

  /** The frame's dock panel, or null where the focus builds none. */
  private dockPanel: HTMLElement | null = null;
  /** The frame's top bar, or null where the focus builds none. */
  private bar: HTMLElement | null = null;
  /** The `--dock-w` written last, so a repeat writes nothing. */
  private dockW = -1;
  private readonly layoutFns: (() => void)[] = [];
  private resizeObs: ResizeObserver | null = null;

  constructor(host: HTMLElement, opts: { bare?: boolean } = {}) {
    this.bare = opts.bare === true;
    this.ids = !this.bare && !document.getElementById('ui');
    this.wrap = document.createElement('div');
    this.wrap.className = 'theme-preview';
    this.wrap.style.cssText =
      'position:relative;width:100%;height:100%;overflow:hidden;isolation:isolate;';
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
    this.chrome = document.createElement('div');
    if (this.ids) this.chrome.id = 'ui';
    this.chrome.style.cssText = 'position:absolute;inset:0;z-index:1;pointer-events:none;'
      + (this.bare ? 'display:none;' : '');
    this.furniture = document.createElement('div');
    if (this.ids) this.furniture.id = 'furniture';
    this.chrome.append(this.furniture);
    this.wrap.append(this.canvas, this.chrome);
    host.append(this.wrap);
    if (typeof ResizeObserver === 'function') {
      this.resizeObs = new ResizeObserver(() => this.publishDock());
      this.resizeObs.observe(this.wrap);
    }
  }

  /** Calls `fn` each time the frame's dock column changes (R1234). */
  onLayout(fn: () => void): void { this.layoutFns.push(fn); }

  /** The focus the frame shows now, or null before the first `show`. */
  get shown(): PreviewFocus | null { return this.focus; }

  /**
   * Shows one focus. The UI tokens and the chrome look go on the chrome
   * root on every call; the elements of a chrome focus are built when the
   * focus changes, or when the look of that focus changes, and every other
   * focus leaves the frame empty over the map.
   */
  show(focus: PreviewFocus, look: PreviewLook, worldName: string, light = LIGHT): void {
    // The frame follows the identity of the page (R3298): in the light
    // identity its chrome wears the light body of the look's theme, as the
    // game chrome does, over a map drawn in the light inks. The theme
    // workshop states the identity of its own preview (R3325), so a dark
    // chrome takes the light mark away again.
    if (light) wearLightOn(this.chrome, lightBodyFor(look.ui));
    else {
      delete this.chrome.dataset.identity;
      applyThemeTo(this.chrome, look.ui);
    }
    this.chromeLook = chromeLookOf(look);
    wearChromeLook(this.chrome, this.chromeLook);
    this.worldName = worldName;
    // The frame's own placement rules select on this (R824), so a focus
    // that stands the notices column beside its dock panel can say so
    // without an inline style a theme block could not override.
    for (const c of Array.from(this.chrome.classList)) {
      if (c.startsWith('preview-') && c !== PREVIEW_STACKED) this.chrome.classList.remove(c);
    }
    this.chrome.classList.add(`preview-${focus}`);
    const id = chromeIdOf(focus, this.chromeLook);
    if (focus === this.focus && id === this.builtWith) return;
    this.clearParts();
    this.focus = focus;
    this.builtWith = id;
    if (this.bare) return;
    this.t = 0;
    this.next = 0;
    if (focus === 'notices') this.buildNotices();
    else if (focus === 'dock') this.buildDock();
    else if (focus === 'banner') this.buildBanner();
    else if (focus === 'ui') this.buildUi();
    this.publishDock();
  }

  /**
   * Writes `--dock-w` on the chrome root: the width from the dock panel's
   * left edge to the frame's right edge, or 0 with no dock panel. A figure
   * that moved tells the chart furniture (R1234).
   */
  private publishDock(): void {
    const d = this.dockPanel;
    const w = d ? Math.max(0, Math.round(this.chrome.clientWidth - d.offsetLeft)) : 0;
    // The dock panel keeps its left edge in the stacked order, so the class
    // moves no figure this reads, and the check cannot loop.
    this.chrome.classList.toggle(PREVIEW_STACKED,
      stackedOrder(d !== null, this.barNeedsPx(), this.chrome.clientWidth, w));
    if (w === this.dockW) return;
    this.dockW = w;
    if (w > 0) this.chrome.style.setProperty('--dock-w', `${w}px`);
    else this.chrome.style.removeProperty('--dock-w');
    for (const fn of this.layoutFns) fn();
  }

  /**
   * The width the top bar needs with its whole world name: its own width
   * and what its name span hides behind the ellipsis. 0 with no bar.
   */
  private barNeedsPx(): number {
    const bar = this.bar;
    if (!bar) return 0;
    const name = bar.firstElementChild as HTMLElement | null;
    const hidden = name ? Math.max(0, name.scrollWidth - name.clientWidth) : 0;
    return bar.offsetWidth + hidden;
  }

  /** One step of the chrome loop. The preview calls it once a frame. */
  tick(dtSec: number): void {
    this.t += dtSec;
    if (this.focus === 'notices') this.tickNotices();
    else if (this.focus === 'dock') this.tickDock();
    else if (this.focus === 'banner') this.tickBanner();
  }

  /** Says in the frame that the browser gave no WebGL2 context. */
  showNoGl(text: string): void {
    const p = document.createElement('div');
    p.textContent = text;
    p.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;'
      + 'justify-content:center;text-align:center;padding:0 16px;color:var(--dim);';
    (this.bare ? this.wrap : this.chrome).append(p);
  }

  /** Takes every element, listener and timer away, and the frame with them. */
  destroy(): void {
    this.clearParts();
    this.focus = null;
    this.builtWith = '';
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.layoutFns.length = 0;
    this.wrap.remove();
  }

  // -- the parts -----------------------------------------------------------

  private part(tag: string, id: string, cls = ''): HTMLElement {
    const e = document.createElement(tag);
    if (this.ids) e.id = id;
    if (cls) e.className = cls;
    this.chrome.append(e);
    this.parts.push(e);
    return e;
  }

  private topbar(): HTMLElement {
    const bar = this.part('div', 'topbar', 'panel');
    // The name ends in an ellipsis in a narrow frame (R2043), so the span
    // carries the full name as its title for a mouse.
    bar.innerHTML = `<span title="${esc(this.worldName)}">${esc(this.worldName)}</span>`;
    this.bar = bar;
    // A late font changes the width the bar needs.
    this.resizeObs?.observe(bar);
    return bar;
  }

  private clearParts(): void {
    this.banner?.destroy();
    this.banner = null;
    if (this.dockin) {
      // `abort` puts the classes back to the docked state before the
      // listeners go, so the next focus starts on a still chrome.
      this.dockin.abort();
      this.dockin.destroy();
      this.dockin = null;
    }
    this.notices?.clear();
    this.notices = null;
    this.timer = null;
    if (this.bar) this.resizeObs?.unobserve(this.bar);
    for (const p of this.parts) p.remove();
    this.parts = [];
    this.dockPanel = null;
    this.bar = null;
  }

  // -- notices ---------------------------------------------------------------

  private buildNotices(): void {
    const bar = this.topbar();
    const style = this.chromeLook.notices;
    const drain = document.createElement('div');
    if (this.ids) drain.id = 'truce';
    const fill = document.createElement('i');
    fill.className = 'fill';
    const tab = document.createElement('span');
    tab.className = 'tab';
    drain.append(fill, tab);
    // The column first: a `column` look builds the lane cell the timer
    // stands in, and the frame runs the game's own placement rule (R824).
    const col = this.part('div', 'notices');
    const notices = new NoticeColumn(col, style);
    notices.setClock(() => hhmmss(WORLD_CLOCK_SEC + this.t));
    (notices.timerCell ?? bar).append(drain);
    this.notices = notices;
    this.timer = new TimerLine(drain, tab, style);
    this.line = 0;
  }

  private tickNotices(): void {
    if (this.timer) {
      // One drain of the line every `DRAIN_SEC`, over a truce of its own.
      const frac = 1 - (this.t % DRAIN_SEC) / DRAIN_SEC;
      this.timer.set(truceClaim(frac * TRUCE_SEC), frac);
    }
    if (this.notices) {
      this.notices.sync(true);
      if (this.t >= this.next) {
        const l = this.lines[this.line % this.lines.length];
        this.line++;
        this.notices.push(l.text, l.opts);
        this.next = this.t + NOTICE_EVERY_SEC;
      }
    }
  }

  // -- the chrome dock-in ----------------------------------------------------

  private buildDock(): void {
    this.topbar();
    this.dockPanel = this.part('div', 'dock', 'panel');
    const col = this.part('div', 'notices');
    this.notices = new NoticeColumn(col, this.chromeLook.notices);
    this.notices.push(SM.site_your_hold, { ms: STANDING_MS });
    this.dockin = new ChromeDockIn(this.chrome, null, this.chromeLook.dock);
    this.dockStep = 'rest';
  }

  private tickDock(): void {
    const d = this.dockin;
    if (!d) return;
    d.tick();
    if (this.dockStep === 'rest' && this.t >= this.next) {
      d.cover();
      this.dockStep = 'out';
      this.next = this.t + DOCK_OUT_SEC;
    } else if (this.dockStep === 'out' && this.t >= this.next) {
      d.dock();
      this.dockStep = 'in';
    } else if (this.dockStep === 'in' && d.state === 'idle') {
      this.dockStep = 'rest';
      this.next = this.t + DOCK_REST_SEC;
    }
  }

  // -- the banner ------------------------------------------------------------

  private buildBanner(): void {
    this.banner = new SeasonBanner(this.part('div', 'banner'), this.chromeLook.banner);
  }

  private tickBanner(): void {
    const b = this.banner;
    if (!b) return;
    if (b.showing) {
      this.next = this.t + BANNER_REST_SEC;
      return;
    }
    if (this.t < this.next) return;
    b.raise({
      kind: 'login',
      worldName: this.worldName,
      season: 1,
      leftSec: PREVIEW_SEASON_LEFT_SEC,
      seasonSec: PREVIEW_SEASON_SEC,
    });
  }

  // -- the UI tokens ---------------------------------------------------------

  private buildUi(): void {
    const bar = this.topbar();
    bar.insertAdjacentHTML('beforeend',
      `<button class="primary">${SO.bar_opts}</button>`);
    const dock = this.part('div', 'dock', 'panel');
    this.dockPanel = dock;
    dock.innerHTML = '<div class="subrow">'
      + `<button class="sub on">${SO.bar_inbox}</button>`
      + `<button class="sub">${SO.bar_music}</button></div>`
      + `<h3>${SM.site_your_hold}</h3>`
      + `<div class="row">${SM.fleet_condition({ hull: SO.bar_pct({ pct: 78 }), shields: SO.bar_pct({ pct: 55 }) })}</div>`
      + `<div class="row" style="color:var(--dim)">${SM.site_point_defense_ready}</div>`
      + `<div class="row" style="color:var(--faint)">${SM.deposit_respawn}</div>`
      + `<button class="primary">${SO.bar_opts}</button>`;
  }
}
